import { SettingsSectionConfig } from "./companyDetailsForm";

/**
 * The GST panel layout.
 *
 * ORDER MATTERS HERE, and it is the order the page reads in:
 *
 *   1. Your GST registration   your own GSTIN — the fact everything else is
 *                              about. GstPage hides this whole section until
 *                              GST is SAVED on, or a GSTIN is already stored
 *                              (items 25 + 27).
 *   2. In Zoho Books           what your books say (rendered by GstPage, not from this list)
 *   3. In Zournal              when GST applies to your documents
 *   4. Checks                  what to look at after switching on (also GstPage)
 *   5. Party GSTIN verification  checking OTHER parties' GSTINs
 *
 * Your own GSTIN and other people's GSTINs are the easiest two things on this
 * screen to conflate, which is why they sit at opposite ends of it.
 *
 * The two blocks GstPage renders itself — "In Zoho Books" and "Checks" — are
 * not in this list because neither is a form: one is read live over the
 * tenant's OAuth token and has its own Refresh, the other is run on demand.
 *
 * What is NOT here, and why:
 *
 *   Registration type    Master-admin reads it from Zoho's organisation flags.
 *   e-invoicing          Read from Zoho per registration.
 *   Filing frequency     A Zournal-only answer in master-admin's wizard.
 *
 * None of the three has a column in the tenant and nothing here reads one, so a
 * control for them would be a control that lies.
 */
export const GST_SECTIONS: SettingsSectionConfig[] = [
  {
    id: "registration",
    title: "Your GST registration",
    description:
      "The GSTIN Zournal puts on every document pushed to your books. It should match Zoho Books.",
    fields: [
      {
        name: "gst_number",
        label: "GSTIN",
        kind: "text",
        // Read-only, and deliberately without the "Locked: …" line or the
        // "Set when your workspace is created…" hint. The server still refuses
        // the write; this page just does not spend two lines explaining it.
        // The Company Details panel keeps both.
        hideLockReason: true,
      },
    ],
  },
  {
    id: "zournal",
    title: "In Zournal",
    description: "When GST applies to the documents Zournal pushes to your books.",
    fields: [
      {
        name: "company_gst_enabled",
        label: "GST applicable",
        kind: "toggle",
        optionsKey: "company_gst_enabled",
        hint: "Turn on after GST is switched on in Zoho Books. Off means no GST on any document.",
      },
      {
        name: "gst_enabled_from",
        label: "GST applies to documents dated on or after",
        kind: "date",
        suggestionKey: "zoho_registered_on",
        suggestionLabel: "Use Zoho's date",
        hint: "Optional. Documents dated earlier are pushed with no GST. Applies to the primary registration and to any registration without its own date on the Locations page.",
      },
    ],
  },
  {
    id: "verification",
    title: "Party GSTIN verification",
    description:
      "Check customer and vendor GSTINs against the GST portal before saving.",
    fields: [
      {
        name: "gstin_verification_required",
        label: "Verification before saving",
        kind: "select",
        optionsKey: "gstin_verification_required",
        hint: "Applies to customers and vendors, including bulk Excel uploads.",
      },
      {
        name: "cashfree_client_id",
        label: "Cashfree client ID",
        kind: "text",
        placeholder: "CF...",
        hint: "From your Cashfree dashboard, under the verification suite.",
      },
      {
        name: "cashfree_client_secret",
        label: "Cashfree client secret",
        kind: "secret",
        placeholder: "Enter the client secret",
        // Deliberately does NOT claim encryption at rest. The column is a plain
        // varchar on company_profile. What is true is that this screen never
        // reads it back, and that is all the copy says.
        hint: "Once saved, this is never shown again. Enter a new one to replace it.",
      },
    ],
  },
];
